import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
    Area,
    AreaChart,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { adminGetIntelOverview } from '../../../api/Api';
import { useColors } from '../../../theme/ColorModeContext';
import type { IntelOverview as Overview, SeriesPoint } from './types';
import {
    CHANNEL_LABELS,
    LEARNING_LABELS,
    RECENCY_LABELS,
    STAGE_HINTS,
    STAGE_LABELS,
    fmtDelta,
    fmtHours,
    fmtNumber,
    fmtPct,
} from './format';
import { BarList, MONO, Section, Tile, TileGrid, TrackingNote, TwoColumns, useTableSx } from './widgets';
import { useChartTheme, withAlpha } from './chartTheme';

interface Props {
    token: string;
    days: number;
    includeAdmins: boolean;
}

interface ChartPoint {
    label: string;
    range: string;
    registered: number;
    confirmed: number;
    total_users: number;
    active_users: number;
    logins: number;
}

/**
 * Past 90 days the series is bucketed by week, so a year reads as 52 bars rather
 * than 365 slivers. A week's active users is the average of its daily actives:
 * unique weekly actives cannot be rebuilt from daily counts.
 */
const toChartPoints = (series: SeriesPoint[], weekly: boolean): ChartPoint[] => {
    if (!weekly) {
        return series.map((point) => ({ ...point, label: point.date.slice(5), range: point.date }));
    }
    const points: ChartPoint[] = [];
    for (let index = 0; index < series.length; index += 7) {
        const chunk = series.slice(index, index + 7);
        const sum = (key: keyof SeriesPoint) => chunk.reduce((total, point) => total + (point[key] as number), 0);
        points.push({
            label: chunk[0].date.slice(5),
            range: `${chunk[0].date} to ${chunk[chunk.length - 1].date}`,
            registered: sum('registered'),
            confirmed: sum('confirmed'),
            logins: sum('logins'),
            total_users: chunk[chunk.length - 1].total_users,
            active_users: Math.round((sum('active_users') / chunk.length) * 10) / 10,
        });
    }
    return points;
};

const IntelOverview = ({ token, days, includeAdmins }: Props) => {
    const colors = useColors();
    const chart = useChartTheme();
    const table = useTableSx();
    const [data, setData] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        adminGetIntelOverview(token, days, includeAdmins).then((result) => {
            if (cancelled) return;
            setData(result);
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [token, days, includeAdmins]);

    const weekly = days > 90;
    const points = useMemo(() => (data ? toChartPoints(data.series, weekly) : []), [data, weekly]);

    if (loading && !data) {
        return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: colors.accent.cyan }} /></Box>;
    }
    if (!data) {
        return <Typography sx={{ color: colors.text.muted }}>Could not load intel. Check that the API is up to date.</Typography>;
    }

    const { totals, time_to_value: ttv, education, acquisition } = data;
    const segmentMax = Math.max(1, ...data.segments.stages.flatMap((stage) => data.segments.recencies.map((recency) => data.segments.counts[stage][recency])));
    const bucket = weekly ? 'week' : 'day';

    return (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <TrackingNote since={data.tracking_since} />

            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.12em', color: colors.text.secondary, mb: 1 }}>GROWTH</Typography>
            <TileGrid>
                <Tile label="Total users" value={fmtNumber(totals.users)} hint={`${fmtNumber(totals.confirmed)} confirmed (${fmtPct(totals.confirmation_rate)})`} />
                <Tile
                    label={`New · ${data.window_days}d`}
                    value={fmtNumber(totals.new_users)}
                    hint={totals.growth_rate == null ? `previous ${data.window_days}d: ${totals.new_users_previous}` : `${fmtDelta(totals.growth_rate)} vs previous ${data.window_days}d`}
                />
                <Tile label="Time to confirm" value={fmtHours(totals.median_hours_to_confirm)} hint="median, signup to confirmation" />
                <Tile label="Activation" value={fmtPct(totals.activation_rate)} hint={`${fmtNumber(totals.activated)} confirmed users with 1+ solve`} />
                <Tile label="Product updates" value={fmtPct(totals.marketing_opt_in_rate)} hint="of all users opted in" />
            </TileGrid>

            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.12em', color: colors.text.secondary, mb: 1 }}>ENGAGEMENT</Typography>
            <TileGrid>
                <Tile label="Active today" value={fmtNumber(totals.active_today)} hint="UTC day so far" />
                <Tile label="Active 7d" value={fmtNumber(totals.wau)} hint="weekly active users" />
                <Tile label="Active 30d" value={fmtNumber(totals.mau)} hint="monthly active users" />
                <Tile label="Stickiness" value={fmtPct(totals.stickiness)} hint="avg daily actives ÷ 30d actives" />
                <Tile label="One-and-done" value={fmtNumber(totals.one_and_done)} hint={`${fmtPct(totals.one_and_done_rate)} of confirmed: 1 active day, gone 7d+`} />
                <Tile label="Never active" value={fmtNumber(totals.never_active)} hint="confirmed, no recorded activity" />
            </TileGrid>

            <TwoColumns>
                <Section title="Signups" caption={`Registrations per ${bucket}, and confirmations on the ${bucket} they happened.`}>
                    <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                                <CartesianGrid {...chart.grid} />
                                <XAxis dataKey="label" {...chart.axis} minTickGap={24} />
                                <YAxis allowDecimals={false} {...chart.axis} />
                                <Tooltip {...chart.tooltip} labelFormatter={(_, payload) => payload?.[0]?.payload?.range ?? ''} />
                                <Legend {...chart.legend} />
                                <Bar dataKey="registered" name="Registered" fill={chart.series[0]} radius={[4, 4, 0, 0]} maxBarSize={18} />
                                <Line dataKey="confirmed" name="Confirmed" stroke={chart.series[1]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                </Section>
                <Section title="Total users" caption="Cumulative registered accounts (current accounts; deleted ones are gone from history).">
                    <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                                <CartesianGrid {...chart.grid} />
                                <XAxis dataKey="label" {...chart.axis} minTickGap={24} />
                                <YAxis allowDecimals={false} {...chart.axis} />
                                <Tooltip {...chart.tooltip} labelFormatter={(_, payload) => payload?.[0]?.payload?.range ?? ''} />
                                <Area dataKey="total_users" name="Total users" stroke={chart.series[0]} strokeWidth={2} fill={withAlpha(chart.series[0], 0.12)} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Section>
            </TwoColumns>

            <Section
                title="Active users"
                caption={weekly
                    ? 'Average daily active users per week. Hover for that week\'s logins.'
                    : 'Users with any signed-in presence or learning action that UTC day. Hover for logins.'}
            >
                <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                            <CartesianGrid {...chart.grid} />
                            <XAxis dataKey="label" {...chart.axis} minTickGap={24} />
                            <YAxis allowDecimals={false} {...chart.axis} />
                            <Tooltip
                                {...chart.tooltip}
                                labelFormatter={(_, payload) => payload?.[0]?.payload?.range ?? ''}
                                formatter={(value, _name, item) => [`${value} active · ${item?.payload?.logins ?? 0} logins`, weekly ? 'Avg daily' : 'Users']}
                            />
                            <Area dataKey="active_users" name="Active users" stroke={chart.series[0]} strokeWidth={2} fill={withAlpha(chart.series[0], 0.12)} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            </Section>

            <TwoColumns>
                <Section
                    title="Activation funnel"
                    caption={`Everyone who registered in the last ${data.window_days} days. Each step counts only users who passed every step above it; the figure on the right is the share of the step before. Recent signups have had less time to get far.`}
                >
                    <BarList
                        rows={data.funnel.map((step) => ({
                            key: step.key,
                            label: step.label,
                            value: step.users,
                            display: `${fmtNumber(step.users)} · ${fmtPct(step.rate)}`,
                            detail: step.key === 'registered' ? undefined : `${fmtPct(step.step_rate)} of prev`,
                        }))}
                        max={data.funnel[0]?.users || 1}
                    />
                </Section>
                <Section title="Do they come back?" caption={`Confirmed users from the same cohort, by number of distinct active days.`}>
                    <BarList
                        rows={data.return_frequency.map((bucketRow) => ({
                            key: bucketRow.key || '',
                            label: bucketRow.label,
                            value: bucketRow.users,
                            display: `${fmtNumber(bucketRow.users)} · ${fmtPct(bucketRow.rate)}`,
                        }))}
                    />
                    <Box sx={{ mt: 3 }}>
                        <TileGrid min={130}>
                            <Tile label="To first action" value={fmtHours(ttv.median_hours_to_first_action)} hint="median from signup" />
                            <Tile label="To first solve" value={fmtHours(ttv.median_hours_to_first_solve)} hint="median from signup" />
                            <Tile label="Days to solve" value={ttv.median_active_days_to_first_solve ?? '-'} hint="median active days before it" />
                        </TileGrid>
                    </Box>
                </Section>
            </TwoColumns>

            <Section
                title="Lifecycle × recency"
                caption="Every user, by how far they have got (rows) and how recently they were active (columns). Activated or committed users who are dormant are the best re-engagement targets; signed-up-only users are an onboarding problem."
            >
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Stage</TableCell>
                                {data.segments.recencies.map((recency) => <TableCell key={recency} align="center">{RECENCY_LABELS[recency]}</TableCell>)}
                                <TableCell align="center">Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.segments.stages.map((stage) => {
                                const row = data.segments.counts[stage];
                                const total = Object.values(row).reduce((sum, n) => sum + n, 0);
                                return (
                                    <TableRow key={stage} sx={table.row}>
                                        <TableCell title={STAGE_HINTS[stage]}>{STAGE_LABELS[stage]}</TableCell>
                                        {data.segments.recencies.map((recency) => {
                                            const n = row[recency];
                                            return (
                                                <TableCell
                                                    key={recency}
                                                    align="center"
                                                    sx={{ fontFamily: MONO, backgroundColor: n ? withAlpha(chart.series[0], 0.06 + 0.5 * (n / segmentMax)) : 'transparent' }}
                                                >
                                                    {n || '·'}
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell align="center" sx={{ fontFamily: MONO, fontWeight: 700 }}>{total}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            <Section
                title="Weekly retention"
                caption="Signup cohorts by week. Week N is the share of the cohort active in the 7 days starting N weeks after each person's own signup day. Blank cells have not happened yet."
            >
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Cohort</TableCell>
                                <TableCell align="right">Users</TableCell>
                                {Array.from({ length: data.retention.weeks + 1 }, (_, week) => <TableCell key={week} align="center">W{week}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.retention.cohorts.map((cohort) => (
                                <TableRow key={cohort.week_start} sx={table.row}>
                                    <TableCell sx={table.mono}>{cohort.week_start}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{cohort.users}</TableCell>
                                    {cohort.rates.map((rate, week) => (
                                        <TableCell
                                            key={week}
                                            align="center"
                                            sx={{ fontFamily: MONO, fontSize: '0.74rem', backgroundColor: rate ? withAlpha(chart.series[0], 0.08 + 0.7 * rate) : 'transparent' }}
                                        >
                                            {cohort.users === 0 || rate == null ? '' : fmtPct(rate)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            <Section
                title="Acquisition"
                caption={`Where the ${fmtNumber(totals.new_users)} users who registered in the last ${data.window_days} days came from (first touch, remembered in the browser for up to 30 days before signup). "Unknown" is accounts created before attribution shipped.`}
            >
                <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Channel</TableCell>
                                <TableCell align="right">Users</TableCell>
                                <TableCell align="right">Confirmed</TableCell>
                                <TableCell align="right">Activated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {acquisition.channels.map((row) => (
                                <TableRow key={row.channel} sx={table.row}>
                                    <TableCell>{CHANNEL_LABELS[row.channel] || row.channel}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.users}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtPct(row.confirmation_rate)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtPct(row.activation_rate)}</TableCell>
                                </TableRow>
                            ))}
                            {!acquisition.channels.length && (
                                <TableRow sx={table.row}><TableCell colSpan={4}>No signups in this window.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                    <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: colors.text.secondary, mb: 1 }}>REFERRING SITES</Typography>
                        <BarList rows={acquisition.referrers.map((row) => ({ key: row.host, label: row.host, value: row.users }))} empty="No referrers recorded." />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: colors.text.secondary, mb: 1 }}>LANDING PAGES</Typography>
                        <BarList rows={acquisition.landing_paths.map((row) => ({ key: row.path, label: row.path, value: row.users }))} empty="No landing pages recorded." />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: colors.text.secondary, mb: 1 }}>CAMPAIGNS (UTM)</Typography>
                        <BarList
                            rows={acquisition.campaigns.map((row) => {
                                const label = [row.source, row.medium, row.campaign].filter(Boolean).join(' / ');
                                return { key: label, label, value: row.users };
                            })}
                            empty="No tagged links used yet."
                        />
                    </Box>
                </Box>
            </Section>

            <Section
                title="How they learn"
                caption={`All ${fmtNumber(education.learners)} users who have opened content, answered, solved or taken a signed-in tech quiz. Tracks group theory stages and challenges by chain.`}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
                    <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: colors.text.secondary, mb: 1 }}>FIRST THING THEY DID</Typography>
                        <BarList rows={education.first_action.map((row) => ({ key: row.kind || '', label: LEARNING_LABELS[row.kind || ''], value: row.users, display: `${row.users} · ${fmtPct(row.rate)}` }))} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: colors.text.secondary, mb: 1 }}>WHAT THEY USE</Typography>
                        <BarList rows={education.mix.map((row) => ({ key: row.kind || '', label: LEARNING_LABELS[row.kind || ''], value: row.users, display: `${row.users} · ${fmtPct(row.rate)}` }))} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: colors.text.secondary, mb: 1 }}>TRACKS PER USER</Typography>
                        <BarList rows={education.tracks_per_user.map((row) => ({ key: row.tracks, label: `${row.tracks} track${row.tracks === '1' ? '' : 's'}`, value: row.users }))} />
                    </Box>
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Track</TableCell>
                                <TableCell align="right">Started</TableCell>
                                <TableCell align="right">Solved any</TableCell>
                                <TableCell align="right">Theory solves</TableCell>
                                <TableCell align="right">Challenge solves</TableCell>
                                <TableCell align="right" title="Users who completed every active theory stage of the track">Theory finished</TableCell>
                                <TableCell align="right" title="Median days from first opening the track's theory to completing its last stage">Days to finish theory</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {education.tracks.map((row) => (
                                <TableRow key={row.track} sx={table.row}>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.users_started}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.users_solved} ({fmtPct(row.users_started ? row.users_solved / row.users_started : null)})</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.theory_solves}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.challenge_solves}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.theory_stages ? `${row.theory_completed}` : '-'}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.median_days_to_complete_theory ?? '-'}</TableCell>
                                </TableRow>
                            ))}
                            {!education.tracks.length && (
                                <TableRow sx={table.row}><TableCell colSpan={7}>Nobody has started a track yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>
        </Box>
    );
};

export default IntelOverview;
